import './DoItSh.css';
import htmlContent from './DoItSh.html?raw';

class DoItSh {
  constructor(container) {
    this.container = container;
    this.form = null;
    this.emailInput = null;
    this.projectTypeInput = null;
    this.socialLinkInput = null;
    this.submitButton = null;
    this.errorMessages = null;
    this.successMessage = null;
    this.isSubmitting = false;
  }

  async render() {
    try {
      this.container.innerHTML = htmlContent;
      this.insertEmailForm();
      this.setupEventListeners();
      
      // Add a subtle load animation
      this.container.style.opacity = '0';
      this.container.style.transform = 'translateY(10px)';
      this.container.style.transition = 'opacity 0.3s ease-out, transform 0.3s ease-out';
      
      setTimeout(() => {
        this.container.style.opacity = '1';
        this.container.style.transform = 'translateY(0)';
      }, 50);
      
    } catch (error) {
      console.error('Error rendering DoItSh component:', error);
      this.container.innerHTML = '<p>Error loading content. Please refresh the page.</p>';
    }
  }

  insertEmailForm() {
    const formSection = this.container.querySelector('.doit-form-section');
    if (formSection) {
      formSection.innerHTML = `
        <form class="neon-terminal-form">
          <div class="terminal-header">
            <span class="terminal-prompt">root@ourappologies:~$</span>
            <span class="terminal-command">collaborate.sh</span>
            <span class="blink">_</span>
          </div>
          
          <div class="input-container">
            <label for="email" class="terminal-prompt">Enter your email:</label>
            <div class="email-wrapper">
              <input type="email" id="email" class="terminal-input" placeholder="you@example.com" required />
            </div>
            <div class="form-error">Please enter a valid email address.</div>
          </div>

          <div class="input-container">
            <label for="project-type" class="terminal-prompt">What brings you here:</label>
            <div class="email-wrapper">
              <select id="project-type" class="terminal-input" required>
                <option value="">Select your interest...</option>
                <option value="creator">Creator Collaboration (500K+ audience monetization)</option>
                <option value="startup">Startup/Entrepreneur (software without overhead)</option>
                <option value="brand">Brand Partnership (experimental projects)</option>
                <option value="engineering">Engineering Challenge (creative + upside)</option>
                <option value="design">Design Collaboration (radical ideas)</option>
                <option value="general">General Inquiry (just chatting)</option>
              </select>
            </div>
            <div class="form-error">Please select what brings you here.</div>
          </div>

          <div class="input-container">
            <label for="social-link" class="terminal-prompt">Your main link (optional):</label>
            <div class="email-wrapper">
              <input type="url" id="social-link" class="terminal-input" placeholder="https://linkedin.com/in/yourname or @yourhandle" />
            </div>
            <div class="form-error">Please enter a valid URL.</div>
          </div>
          
          <button type="submit" class="glitch-button">
            <span class="button-text">LET'S BUILD</span>
          </button>
          
          <div class="terminal-footer">
            <span class="green-text">// We protect your email like our production keys.</span>
          </div>
          
          <div class="form-success">
            <div class="ascii-checkmark">
              Vibe check pending...
            </div>
            <p>Message sent! We'll be in touch soon.</p>
          </div>

          <div class="form-error-general">
            <p>Something went wrong. Please try again or email us directly at <a href="mailto:vibecheck@ourappologies.com">vibecheck@ourappologies.com</a></p>
          </div>
        </form>
      `;
    }
  }

  setupEventListeners() {
    // Cache DOM elements
    this.form = this.container.querySelector('.neon-terminal-form');
    this.emailInput = this.container.querySelector('#email');
    this.projectTypeInput = this.container.querySelector('#project-type');
    this.socialLinkInput = this.container.querySelector('#social-link');
    this.submitButton = this.container.querySelector('.glitch-button');
    this.errorMessages = this.container.querySelectorAll('.form-error');
    this.successMessage = this.container.querySelector('.form-success');
    this.generalErrorMessage = this.container.querySelector('.form-error-general');

    if (this.form) {
      this.form.addEventListener('submit', this.handleSubmit.bind(this));
    }
    
    // Hide error messages when user interacts with inputs
    if (this.emailInput) {
      this.emailInput.addEventListener('input', () => {
        this.errorMessages[0].style.display = 'none';
        this.hideGeneralError();
      });
    }

    if (this.projectTypeInput) {
      this.projectTypeInput.addEventListener('change', () => {
        this.errorMessages[1].style.display = 'none';
        this.hideGeneralError();
      });
    }

    if (this.socialLinkInput) {
      this.socialLinkInput.addEventListener('input', () => {
        this.errorMessages[2].style.display = 'none';
        this.hideGeneralError();
      });
    }
  }

  /**
   * Handle form submission with Google Apps Script integration
   * @param {Event} event - Form submit event
   */
  async handleSubmit(event) {
    event.preventDefault();
    
    if (this.isSubmitting) return; // Prevent double submission
    
    const email = this.emailInput.value.trim();
    const projectType = this.projectTypeInput.value;
    const socialLink = this.socialLinkInput.value.trim();
    
    // Reset all error states
    this.hideAllErrors();
    
    let hasError = false;
    
    if (!this.validateEmail(email)) {
      this.errorMessages[0].style.display = 'block';
      hasError = true;
    }
    
    if (!projectType) {
      this.errorMessages[1].style.display = 'block';
      hasError = true;
    }
    
    // Validate social link only if provided (it's optional)
    if (socialLink && !this.validateUrl(socialLink)) {
      this.errorMessages[2].style.display = 'block';
      hasError = true;
    }
    
    if (hasError) {
      return;
    }
    
    // Start submission
    this.isSubmitting = true;
    this.updateSubmitButton('PROCESSING...');
    
    try {
      const formData = {
        email,
        reason: projectType,
        link: socialLink || '',
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        source: 'ourappologies-collaboration-form'
      };
      
      const response = await this.submitToGoogleAppsScript(formData);
      
      if (response.success) {
        this.showSuccess();
        this.form.reset();
      } else {
        throw new Error(response.error || 'Submission failed');
      }
      
    } catch (error) {
      console.error('Form submission error:', error);
      this.showGeneralError();
    } finally {
      this.isSubmitting = false;
      this.updateSubmitButton('LET\'S BUILD');
    }
  }

  /**
   * Submit form data to Google Apps Script
   * @param {Object} formData - Form data to submit
   * @returns {Promise<Object>} - Response from Apps Script
   */
  async submitToGoogleAppsScript(formData) {
    const endpoint = import.meta.env.VITE_FORM_ENDPOINT || 'APPS_SCRIPT_URL_PLACEHOLDER';
    
    // Debug logging for development
    console.log('🔧 Environment check:');
    console.log('- VITE_FORM_ENDPOINT:', import.meta.env.VITE_FORM_ENDPOINT);
    console.log('- Using endpoint:', endpoint);
    console.log('- Mode:', import.meta.env.MODE);
    
    if (endpoint === 'APPS_SCRIPT_URL_PLACEHOLDER') {
      // Development mode - simulate submission
      console.log('Development mode - simulating form submission:', formData);
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({ success: true, message: 'Development submission successful' });
        }, 1500);
      });
    }
    
    console.log('🚀 Submitting to Google Apps Script:', endpoint);
    
    // Use text/plain to avoid CORS preflight
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain',
      },
      body: JSON.stringify(formData)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    console.log('📨 Response from Apps Script:', result);
    return result;
  }

  /**
   * Update submit button text and state
   * @param {string} text - Button text to display
   */
  updateSubmitButton(text) {
    if (this.submitButton) {
      this.submitButton.disabled = this.isSubmitting;
      this.submitButton.innerHTML = `<span class="button-text">${text}</span>`;
    }
  }

  /**
   * Hide all error messages
   */
  hideAllErrors() {
    this.errorMessages.forEach(error => error.style.display = 'none');
    this.hideGeneralError();
  }

  /**
   * Hide general error message
   */
  hideGeneralError() {
    if (this.generalErrorMessage) {
      this.generalErrorMessage.style.display = 'none';
    }
  }

  /**
   * Show general error message
   */
  showGeneralError() {
    if (this.generalErrorMessage) {
      this.generalErrorMessage.style.display = 'block';
    }
  }

  /**
   * Validate email format
   * @param {string} email - Email to validate
   * @returns {boolean} - Whether email is valid
   */
  validateEmail(email) {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  }

  /**
   * Validate URL format (basic validation)
   * @param {string} url - URL to validate
   * @returns {boolean} - Whether URL is valid
   */
  validateUrl(url) {
    // Accept URLs starting with http/https or social handles starting with @
    if (url.startsWith('@')) {
      return url.length > 1; // Must have content after @
    }
    
    try {
      new URL(url);
      return true;
    } catch {
      // If it's not a valid URL, check if it looks like a social handle or username
      return /^[a-zA-Z0-9._-]+$/.test(url);
    }
  }

  /**
   * Show success message
   */
  showSuccess() {
    // Hide the form elements
    this.form.querySelectorAll(':not(.form-success)').forEach(el => {
      if (!el.classList.contains('form-success') && el.parentNode === this.form) {
        el.style.display = 'none';
      }
    });
    
    // Show success message
    this.successMessage.style.display = 'block';
    
    // Add typing animation class
    const message = this.successMessage.querySelector('p');
    if (message) {
      message.classList.add('typing-text');
      message.innerHTML = `<span>${message.textContent}</span>`;
    }
  }
}

export default DoItSh; 